import React from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';

import { fromJS } from 'immutable';

import { updateProfiles } from 'flow/actions/profile-actions';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';

import { sortProfilesAfterPlusId } from 'flow/utils/profile-utils';

import BaseModal from '../ui-components/BaseModal';

import { ExtensionKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';

interface Props {
    extensionKey?: ExtensionKey;
}

const IncomingCallsModal = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { profiles } = useProfiles(extensionKey);

    const updateProfile = (
        profileKey: string,
        profileRef: TypedMap<Profile>,
    ) => {
        const profilesToUpdate = {
            parallelCalls: profileRef.get('parallelCalls'),
            callForwarding: profileRef
                .get('callForwarding')
                .delete('errorRedirect'),
            callMVoip: profileRef.get('callMVoip'),
            spokenNotification: {
                referActionType: profileRef.getIn([
                    'spokenNotification',
                    'referActionType',
                ]),
                referPhraseType: profileRef.getIn([
                    'spokenNotification',
                    'referPhraseType',
                ]),
                announceReferUntilTime: profileRef.getIn([
                    'spokenNotification',
                    'announceReferUntilTime',
                ]),
            },
        };

        return fromJS({ key: profileKey }).mergeDeep(profilesToUpdate);
    };

    const updateNonAvailableProfiles = () => {
        // We don't want to edit the Available profile and plusid 1 is the reference profile, lets skip these.
        const profileRef = profiles.find(
            (profile) => profile.get('plusId') === 1,
        );

        const updatedProfiles = profiles
            .skip(2)
            .map((profile) => updateProfile(profile.get('key'), profileRef));

        // Since we only edited the profiles from plusid 2 and up we still want to use the others for reference so that
        // we get the latest data and our reducers are happy.
        // By sending the whole DTO you risk ending up with errors with settings rights that these settings don't include.
        const nonUpdated = profiles
            .take(2)
            .map((profile) => fromJS({ key: profile.get('key') }));

        dispatch(
            updateProfiles(extensionKey, nonUpdated.concat(updatedProfiles)),
        );
    };

    const firstProfileName =
        !sortProfilesAfterPlusId(profiles).isEmpty() &&
        sortProfilesAfterPlusId(profiles).first().get('description');
    const secondProfileName =
        !sortProfilesAfterPlusId(profiles).isEmpty() &&
        sortProfilesAfterPlusId(profiles).skip(1).first().get('description');
    const resetWarningText = i18next.t(
        'settings.reset.warning.incoming_calls',
        {
            currentSettings: i18next.t('settings.incoming_calls'),
            firstProfileName,
            secondProfileName,
        },
    );

    return (
        <BaseModal
            open={true}
            header={i18next.t('settings.reset.warning.general.Title', {
                currentSettings: i18next.t('settings.incoming_calls'),
            })}
            onSubmit={updateNonAvailableProfiles}
        >
            <p
                dangerouslySetInnerHTML={{
                    __html: resetWarningText,
                }}
            />
        </BaseModal>
    );
};

export default IncomingCallsModal;
